import { LiveAnnouncer } from "@angular/cdk/a11y";
import { Component, effect, inject, input, untracked } from "@angular/core";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";

/** How long an announcement stays in CDK's live region before it's cleared, so a screen-reader
 * user reading through the page later doesn't keep finding "… loaded". */
export const ANNOUNCEMENT_DURATION_MS = 5000;

/**
 * Tells screen readers that a page or dialog is loading, and when it has loaded (#6305): "Loading
 * monthly summary", then "Monthly summary loaded" (or `failedKey` if it failed). Announced through
 * CDK's LiveAnnouncer, one shared live region that takes each message just after it's in the page
 * (so it's read even on arrival) and is exposed inside aria-modal dialogs. The skeletons this
 * replaces as the announcement are left aria-hidden: a status region inserted holding its text and
 * removed once loaded (with aria-busy="true" holding back its updates too) was read neither way.
 *
 * Use one per page or dialog, following its main content: several loading together would each
 * queue their own pair of messages. The end of a load is only announced if its start was, for the
 * same `loadingKey`: switching to a view that's already loaded isn't announced as one that just
 * finished.
 */
@Component({
  selector: "app-loading-announcer",
  standalone: true,
  // Renders nothing (the live region is CDK's): hidden, so it takes no grid cell or flex gap.
  host: { hidden: "" },
  template: "",
})
export class LoadingAnnouncerComponent {
  private readonly i18n = inject(I18nService);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  readonly loading = input.required<boolean>();
  /** i18n key announced when loading starts, e.g. "loadingMonthlySummary". */
  readonly loadingKey = input.required<string>();
  /** i18n key announced when it has loaded, e.g. "monthlySummaryLoaded". */
  readonly loadedKey = input.required<string>();
  /** Whether the load that just ended failed: then `failedKey` is announced instead. */
  readonly failed = input(false);
  readonly failedKey = input<string | null>(null);

  // The loadingKey whose start was announced and whose end hasn't been yet.
  private announcedLoading: string | null = null;

  constructor() {
    effect(() => {
      const loading = this.loading();
      const loadingKey = this.loadingKey();
      untracked(() => {
        if (loading) {
          if (this.announcedLoading !== loadingKey) {
            this.announcedLoading = loadingKey;
            this.announce(loadingKey);
          }
          return;
        }
        if (this.announcedLoading === loadingKey) {
          const failedKey = this.failedKey();
          this.announce(this.failed() && failedKey ? failedKey : this.loadedKey());
        }
        this.announcedLoading = null;
      });
    });
  }

  private announce(key: string) {
    void this.liveAnnouncer.announce(this.i18n.t(key), "polite", ANNOUNCEMENT_DURATION_MS);
  }
}
